import { DataTable, FormGroupHeader, Pagination } from "@/components/shared";
import { getColumns } from "./columns";
import {
  GetPaginatedUsersDocument,
  GetPaginatedUsersQuery,
  GetPaginatedUsersQueryVariables,
  Organization,
  PaginatedOrganizationsDocument,
  PaginatedOrganizationsQuery,
  PaginatedOrganizationsQueryVariables,
  Role,
  User,
  UserEdge,
} from "@/graphql/__generated__/graphql";
import { useIntl } from "react-intl";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { ConfirmRemoveAlert } from "@/pages/EditTender/Dialog";
import { ActionButtonConfig } from "@/components/shared/FormGroupHeader";
import { isManager } from "@/utils/authentication";
import { useCreateUserMutationHook, useRemoveUserMutationHook } from "@/hooks";
import { nonEmptyString, optionalString } from "@/utils/form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import UsersFilter from "./Components/UsersFilter";
import { t as translate } from "i18next";
import { SendInviteDialog, CreateUserDialog } from "./Modals";
import { useQuery } from "@apollo/client/react";

interface PaginationRef {
  triggerHandleFilterSubmit: (_newVariables: Partial<GetPaginatedUsersQueryVariables>) => void;
}

const internationalEmailRegex =
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/u; // Allows Unicode in the local part

export const createUserSchema = z.object({
  firstname: nonEmptyString(translate("modals:createuser.inputs.firstname.label")),
  lastname: nonEmptyString(translate("modals:createuser.inputs.lastname.label")),
  email: z
    .string()
    .refine((val) => internationalEmailRegex.test(val), {
      message: translate("schemas:createuser.invalidemail"),
    }),
  role: z.enum([Role.Viewer, Role.Editor]),
  organizationId: optionalString("Organization"),
});

export const createUserDefaultValues = {
  firstname: "",
  lastname: "",
  email: "",
  role: undefined,
  organizationId: undefined,
};

export const FormSchema = z.object({
  ...createUserSchema.shape,
});

export const defaultValues = {
  ...createUserDefaultValues,
};

const UsersComponent = () => {
  // Initialize form object
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues,
    mode: "onChange"
  });

  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["translation", "components", "tables", "common"]);

  // Mutation hooks
  const { removeUserMutation, loading: removeUserLoading } = useRemoveUserMutationHook();
  const { createUserMutation, loading: createUserLoading } = useCreateUserMutationHook();

  // Fetch organization data
  const { data, loading } = useQuery<
    PaginatedOrganizationsQuery,
    PaginatedOrganizationsQueryVariables
  >(PaginatedOrganizationsDocument, {
    variables: {
      first: 100,
      after: null,
    },
  });

  // States
  const [currentUserID, setCurrentUserID] = useState<number | undefined>();
  const [showConfirmRemoveAlert, setShowConfirmRemoveAlert] = useState<boolean>(false);
  const [showCreateUserDialog, setShowCreateUserDialog] = useState<boolean>(false);
  const [showSendInviteDialog, setShowSendInviteDialog] = useState<boolean>(false);
  // Pagination config
  const paginationRef = useRef<PaginationRef | null>(null);
  const paginationName = "paginatedUsers";

  const onFilterSubmit = (variables: GetPaginatedUsersQueryVariables) => {
    // When the filter is submitted in the PageComponent,
    // this will call the handleFilterSubmit inside Pagination.
    paginationRef.current?.triggerHandleFilterSubmit(variables);
  };

  /**
   * Handler for the Remove button in the DataTable
   * @param {User} data
   * @returns
   */
  const handleRemoveClick = (data: User) => {
    setCurrentUserID(data.id);
    setShowConfirmRemoveAlert(true);
  };

  /**
   * Handler function for the `removeUserMutation`
   */
  const handleRemoveUser = () => {
    // Fire mutation
    removeUserMutation({
      variables: {
        userId: currentUserID
      }
    });
  };

  /**
   * Handler for the Send Invite button in the DataTable
   * @param {User} data
   * @returns
   */
  const handleSendInviteClick = (data: User) => {
    setCurrentUserID(data.id);
    setShowSendInviteDialog(true);
  };

  /**
   * Handler for the New Rule button
   */
  const handleCreateUserClick = () => {
    // Open the new rule dialog
    setShowCreateUserDialog(true);
  };

  /**
   * Handler function for adding a car to a service
   * @param {z.infer<typeof FormSchema>} data
   */
  const onSubmit = (data: z.infer<typeof FormSchema>) => {
    const { organizationId, ...rest } = data;
    createUserMutation({
      variables: {
        input: {
          ...rest,
          organizationId: Number(organizationId)
        }
      }
    }).then((res) => {
      // Only proceed if there are no errors
      if (!res.errors) {
        // Clear form
        form.reset(createUserDefaultValues);
        // Close modal
        setShowCreateUserDialog(false);
      }
    });
  };

  /**
   * Action buttons for FormGroupHeader
   * Buttons are conditionally rendered based on the current state
   */
  const actionButtons: ActionButtonConfig[] = [
    isManager() ? { text: t("settings.users.createuser"), actionFn: handleCreateUserClick, variant: "default" } : null,
  ].filter(Boolean) as ActionButtonConfig[];

  return (
    <div>
      <FormGroupHeader
        className="pr-4 pl-2"
        actionButtons={actionButtons}
        title={t("settings.users.title")}
        description={t("settings.users.description")}
      />

      {/* Users table */}
      <div className="pr-4 pl-2 select-text">
        <Pagination<UserEdge, GetPaginatedUsersQuery, GetPaginatedUsersQueryVariables>
          ref={paginationRef}
          props={{
            queryDocument: GetPaginatedUsersDocument,
            paginationName,
            className: "mb-4",
          }}
        >
          {(dataSlice, loading, displayAmount) => (
            <>
              {/* Users filter */}
              <div>
                <UsersFilter
                  organizationsLoading={loading}
                  onFilterSubmit={onFilterSubmit}
                  displayAmount={displayAmount as number}
                  organizationsData={
                    (data?.paginatedOrganizations?.edges.map(
                      (edge) => edge.node
                    ) as Organization[]) ?? []
                  }
                />
                <DataTable
                  disableRowColour={true}
                  loading={loading}
                  displayAmount={displayAmount as number}
                  data={dataSlice.map((edge) => edge.node) ?? []}
                  // defaultSortBy={{ id: "firstname", desc: true }}
                  columns={getColumns(
                    intl,
                    t,
                    handleSendInviteClick,
                    handleRemoveClick,
                    removeUserLoading
                  )}
                  className="max-h-[calc(100vh-20rem)]"
                />
              </div>
            </>
          )}
        </Pagination>
      </div>

      {/* Confirm Remove Service Alert */}
      <ConfirmRemoveAlert
        open={showConfirmRemoveAlert}
        onOpenChange={setShowConfirmRemoveAlert}
        actionButtonFn={handleRemoveUser}
        bodyText={t("components:alertdialogs.removeuser")}
      />

      <CreateUserDialog
        form={form}
        onSubmit={onSubmit}
        open={showCreateUserDialog}
        organizationsLoading={loading}
        onOpenChange={setShowCreateUserDialog}
        organizationsData={data?.paginatedOrganizations?.edges.map((edge) => edge.node) as Organization[] ?? []}
        isProcessing={createUserLoading}
      />

      <SendInviteDialog
        open={showSendInviteDialog}
        onOpenChange={setShowSendInviteDialog}
        userId={currentUserID}
      />
    </div>
  );
};

export default UsersComponent;
